import {
  BranchQuoteSupport,
  BranchQuoteSupportStatus,
  ResultGetBranchQuoteSupport
} from '../../api/dealer-api-interface-franchisee';
import { customElement, property, query, state } from 'lit/decorators.js';
import { html } from 'lit';
import { LitElementBase } from '../../../webmodule-common/other/litelement-base';

import { WebModuleTable, WebModuleTableColumnDef } from '../../../webmodule-common/components/src/webmodule-components';

import { buildModifiedTime, getSupportStatusBadge, getSupportTypeDisplayValue } from './conversation-helper';
// support-events deprecated – no DOM events emitted for inbox actions
import { cache } from '../cache-impl/cache-registry';
import { FranchiseeQuoteContainerManager } from '../quotes/data/franchisee-quote-manager';
import {
  serverDateTimeToLocal,
  serverDateTimeToLocalDateTime
} from '../../../webmodule-common/other/datetime-converter';

import { LangDealerFranchiseeConversationWmBranchquoteSupport } from '../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-support-lang';
import { capabilityReasonsKey, deriveSupportCapabilities, renderSupportDebugBanner } from './support-capabilities';
import { SupportTicketsDelta, SupportTicketsState } from './support-ticket-service';

// NOTE: Inline editor logic and OpenTicketHandler coupling removed – this component is now a pure list.

@customElement('wmview-branchquote-support')
export class WebModuleQuoteSupportInboxView extends LitElementBase {
  @property({ attribute: false }) quoteManager?: FranchiseeQuoteContainerManager; // object reference, no attribute
  @property({ type: String }) selectedTicketId?: string; // highlight only

  @property() supplierName = '';

  @query('#branchquote-support-inbox-table')
  table?: WebModuleTable;

  @state()
  private _results: ResultGetBranchQuoteSupport | undefined;
  private get results(): ResultGetBranchQuoteSupport | undefined {
    return this._results;
  }

  private set results(value: ResultGetBranchQuoteSupport | undefined) {
    this._results = value;
  }

  get data() {
    return this.results?.items;
  }

  private _unsubscribeState?: () => void;

  public async refreshData() {
    if (this.table) {
      this.table?.fetchEvent();
    }
  }

  protected eventTicketClick = (e: CustomEvent<{ item: BranchQuoteSupport }>) => {
    const id = e.detail.item.id;
    // Emit lightweight selection event for legacy consumers (optional highlight only)
    this.selectedTicketId = id;
    // New explicit open intent event; parent should create editor page and then set active ticket
    this.dispatchEvent(
      new CustomEvent('support:open-ticket', {
        detail: { id },
        bubbles: true,
        composed: true
      })
    );
  };

  /** This is called by the table when it wants new page data */
  private async internalDataLoad(e: CustomEvent) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const table = e.detail.table as WebModuleTable;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const index = (e.detail?.pageIndex as number) ?? 0;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const length = (e.detail?.pageLength as number) ?? 100;

    // Unified path: always use service list (no legacy fallback)

    if (this.quoteManager?.branchQuote) {
      const svcResult = await this.quoteManager.needsQuoteSupport();
      // = await service.listQuoteLevel({ quoteSetId, quoteId: id });
      this.results = svcResult ?? undefined;
    } else {
      this.results = undefined; // no quote context available
    }
    if (!this.data) {
      table.data = [];
    }
    if (this.data) {
      //preload userids
      await cache().userProfile.preFetch(this.data.map(x => x.lastModifiedUserId));

      table.data = this.data.slice(index * length, index * length + length);
      table.rowCount = this.data.length;
      // maintain highlight if present
      if (this.selectedTicketId) {
        const stillExists = this.data.find(d => d.id === this.selectedTicketId);
        if (!stillExists) this.selectedTicketId = undefined;
      }
    } else table.data = [];
  }

  renderTableView() {
    const keyEvent = (item: BranchQuoteSupport) => {
      return item.id;
    };
    // Column sizing moves from Bootstrap `colpx-*` classes (which don't pierce the
    // new table's shadow DOM) to the column's own `minWidth` model.
    const columns: WebModuleTableColumnDef[] = [];
    columns.push({
      title: () => LangDealerFranchiseeConversationWmBranchquoteSupport.supportSubject(),
      minWidth: '240px',
      fieldName: 'subject',
      displayValue: (_table: WebModuleTable, item: BranchQuoteSupport) => {
        return html`<span>${item.subject}</span>`;
      }
    });
    columns.push({
      title: () => LangDealerFranchiseeConversationWmBranchquoteSupport.type(),
      minWidth: '90px',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: BranchQuoteSupport) => getSupportTypeDisplayValue(item.type)
    });
    columns.push({
      title: () => LangDealerFranchiseeConversationWmBranchquoteSupport.status(),
      minWidth: '120px',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: BranchQuoteSupport) => getSupportStatusBadge(item.status)
    });
    columns.push({
      title: () => LangDealerFranchiseeConversationWmBranchquoteSupport.created(),
      minWidth: '150px',
      fieldName: 'dateCreated',
      displayValue: (_table: WebModuleTable, item: BranchQuoteSupport) =>
        serverDateTimeToLocalDateTime(item.dateCreated).toLocaleString()
    });
    columns.push({
      title: () => LangDealerFranchiseeConversationWmBranchquoteSupport.modified(),
      minWidth: '180px',
      fieldName: 'branchName',
      displayValue: (_table: WebModuleTable, item: BranchQuoteSupport) => {
        const label = cache().userProfile.getLocalData(item.lastModifiedUserId)?.friendlyName ?? '';
        const dateValue = buildModifiedTime(serverDateTimeToLocalDateTime(item.lastModified));
        // Elapsed time on the first line, the user on its own line in italics.
        // Wrap both in one block so they stack vertically inside the flex-row cell.
        return html`<div class="lastmodified-cell">
          <div class="lastmodified-date">${dateValue}</div>
          <div class="lastmodified-user"><em>${label}</em></div>
        </div>`;
      }
    });
    columns.push({
      title: () => LangDealerFranchiseeConversationWmBranchquoteSupport.dateResolved(),
      minWidth: '110px',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: BranchQuoteSupport) => {
        const resolvedDate =
          item.status === BranchQuoteSupportStatus.Resolved
            ? serverDateTimeToLocal(item.lastModified).toLocaleDateString()
            : '-';

        return resolvedDate;
      }
    });
    columns.push({
      title: () => LangDealerFranchiseeConversationWmBranchquoteSupport.resolution(),
      minWidth: '110px',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: BranchQuoteSupport) => {
        const resolvedDate =
          item.status === BranchQuoteSupportStatus.Resolved
            ? buildModifiedTime(serverDateTimeToLocalDateTime(item.lastModified))
            : '-';
        return resolvedDate;
      }
    });

    return html` <div class="branchquote-support-inbox">
      <webmodule-table
        id="branchquote-support-inbox-table"
        .keyevent=${keyEvent}
        .itemRowClassEvent=${(item: object) =>
          (item as BranchQuoteSupport).id === this.selectedTicketId ? 'selected-row' : ''}
        .clickrows=${true}
        .elevated=${false}
        .cellBorders=${false}
        .uppercaseHeader=${false}
        .customStyles=${'.wm-table-row.selected-row { background: var(--wm-table-row-selected); }'}
        @rowclick=${this.eventTicketClick}
        .columns=${columns as any}
        pageLength="600"
        @fetch=${(e: CustomEvent) => this.internalDataLoad(e)}
      >
      </webmodule-table>
    </div>`;
  }

  render() {
    const evalResult = this.evaluateCapabilities();
    const innerTemplate = this.renderTableView();
    const debugBanner = renderSupportDebugBanner(evalResult, 'Inbox');

    return html` <div class="branchquote-support-inbox">${debugBanner}${innerTemplate}</div>`;
  }

  connectedCallback(): void {
    super.connectedCallback();
    // Subscribe to service snapshots for lightweight row patching (no full refetch)
    const svc = this.quoteManager?.supportTicketService;
    if (svc) {
      this._unsubscribeState = svc.subscribe((state: SupportTicketsState, delta: SupportTicketsDelta) => {
        if (!delta.changedTicketIds.length) return; // ignore non-ticket deltas (draft, activeTicket, etc.)
        if (!this.results?.items) return; // nothing loaded yet
        let mutated = false;
        for (const id of delta.changedTicketIds) {
          const snap = state.tickets[id];
          if (!snap || !snap.ticket) continue;
          const idx = this.results.items.findIndex(it => it.id === id);
          if (idx >= 0) {
            // Patch only changed lightweight fields to preserve array & avoid full re-render cost
            const target = this.results.items[idx];
            const src = snap.ticket;
            // Minimal diff check – if status or subject or lastModified changed, mutate
            if (
              target.status !== src.status ||
              target.subject !== src.subject ||
              target.lastModified !== src.lastModified ||
              target.lastModifiedUserId !== src.lastModifiedUserId ||
              target.recordVersion !== src.recordVersion
            ) {
              // Shallow copy fields we care about (extend as needed)
              target.status = src.status;
              target.subject = src.subject;
              target.lastModified = src.lastModified;
              target.lastModifiedUserId = src.lastModifiedUserId;
              (target as any).recordVersion = (src as any).recordVersion; // if present
              mutated = true;
            }
          }
        }
        if (mutated) {
          // If table already has data slice, update those rows too
          if (this.table?.data) {
            const slice = this.table.data as BranchQuoteSupport[];
            for (let i = 0; i < slice.length; i++) {
              const id = slice[i].id;
              const snap = state.tickets[id];
              if (snap?.ticket) {
                const src = snap.ticket;
                const row = slice[i];
                if (
                  row.status !== src.status ||
                  row.subject !== src.subject ||
                  row.lastModified !== src.lastModified ||
                  row.lastModifiedUserId !== src.lastModifiedUserId
                ) {
                  row.status = src.status;
                  row.subject = src.subject;
                  row.lastModified = src.lastModified;
                  row.lastModifiedUserId = src.lastModifiedUserId;
                }
              }
            }
          }
          this.requestUpdate();
        }
      });
    }
  }

  disconnectedCallback(): void {
    try {
      this._unsubscribeState?.();
    } catch {
      /* ignore */
    }
    super.disconnectedCallback();
  }
  protected updated(changed: Map<string, unknown>): void {
    super.updated?.(changed as any);
    // no inline editor state to sync anymore
  }

  // --- Capability Evaluation & Debug Banner (Layered readonly adoption) ---
  private _lastReasonsKey = '';
  private evaluateCapabilities() {
    const evalResult = deriveSupportCapabilities({
      quoteManager: this.quoteManager,
      ticketStatus: undefined,
      forceReadonly: this.isReadonly,
      transientBusy: false,
      phase: 'inbox'
    });
    const key = capabilityReasonsKey(evalResult);
    if (key !== this._lastReasonsKey) {
      this._lastReasonsKey = key;
      // Notify local listeners only (legacy global event removed)
      this.dispatchCustom('support-inbox-capabilities-changed', {});
    }
    return evalResult;
  }
}
